'use client';

import { useSelector, withFieldGroup } from '@repo/form';
import type { GetLoadDuplicateTemplate } from '@repo/schema/load-duplicate';
import { ButtonGroup, Field, FieldError, FieldLabel, Input } from '@repo/ui/components';
import { DateFormatter } from '@repo/ui/date-formatter';
import { CalendarIcon } from '@repo/ui/icons';
import { cn } from '@repo/ui/lib/utils';
import { Fragment, type ReactNode, useId } from 'react';
import { CurrencySelect } from '@/app/@protected/loads/[id]/(details)/overview/_components/bills-card/_components/carrier-rate/currency-select.tsx';
import { type DuplicateCardValues, type DuplicateDateRules, getDuplicateCardSummary } from '@/app/@protected/loads/_utils/duplicate-card.ts';
import { DuplicateStopFields } from './duplicate-stop-fields.tsx';
const DEFAULT_VALUES: DuplicateCardValues = {
  customer_rate_amount: '',
  customer_rate_currency: '',
  drops: []
};
type DuplicateCardProps = {
  dateRules: DuplicateDateRules;
  /** The choice of the Original Load's dates, at the start of the column's dates section. */
  datesOption?: ReactNode;
  /** A refusal that belongs to this Duplicate rather than to one of its fields. */
  error?: ReactNode;
  /** `grid`: one row per Stop, as the dialog shows it; `column`: one field under another. */
  layout: 'column' | 'grid';
  pending: boolean;
  /** The choice of the Original Load's rate, at the start of the column's rate section. */
  rateOption?: ReactNode;
  /** The template's Stops, which pair with the card's by position. */
  templateStops: GetLoadDuplicateTemplate['drops'];
};
const DUPLICATE_CARD_PROPS: DuplicateCardProps = {
  dateRules: {
    canSetPastDates: false,
    today: new Date()
  },
  datesOption: undefined,
  error: undefined,
  layout: 'grid',
  pending: false,
  rateOption: undefined,
  templateStops: []
};

// The ledger's columns once the card has room for them: the Stop's number, then its date, time and
// reference.
const LEDGER_COLUMNS = 'grid grid-cols-[minmax(0,1fr)] gap-x-3 @min-[48rem]/duplicate-card:grid-cols-[1.5rem_minmax(0,1fr)_minmax(min-content,1fr)_minmax(0,1fr)]';
type RateValues = Pick<DuplicateCardValues, 'customer_rate_amount' | 'customer_rate_currency'>;
const RATE_DEFAULT_VALUES: RateValues = {
  customer_rate_amount: '',
  customer_rate_currency: ''
};
type DuplicateCardRateProps = {
  /** The label beside the fields, as the dialog's card title line shows it, rather than above. */
  inline?: boolean;
  pending: boolean;
};
const DUPLICATE_CARD_RATE_PROPS: DuplicateCardRateProps = {
  inline: false,
  pending: false
};

/** A Duplicate's customer rate and its currency. */
export const DuplicateCardRate = withFieldGroup({
  defaultValues: RATE_DEFAULT_VALUES,
  props: DUPLICATE_CARD_RATE_PROPS,
  render: function Render({
    group,
    inline,
    pending
  }) {
    const id = useId();
    return <group.Field name="customer_rate_amount">
        {field => <Field className={inline ? 'w-fit items-start gap-3' : 'gap-1.5'} orientation={inline ? 'horizontal' : 'vertical'}>
            <FieldLabel className={cn(inline && 'h-8 items-center whitespace-nowrap')} htmlFor={id}>
              Customer rate &amp; currency
            </FieldLabel>
            <div className="flex flex-col gap-1.5">
              <ButtonGroup className={inline ? 'w-56' : 'w-full'}>
                <Input disabled={pending} id={id} inputMode="decimal" min="0" onBlur={field.handleBlur} onChange={event => field.handleChange(event.target.value)} placeholder="Enter rate" step="0.01" type="number" value={field.state.value} />
                <group.Field name="customer_rate_currency">
                  {currencyField => <CurrencySelect disabled={pending} onChange={currencyField.handleChange} value={currencyField.state.value} />}
                </group.Field>
              </ButtonGroup>
              <FieldError errors={field.state.meta.errors} />
            </div>
          </Field>}
      </group.Field>;
  }
});

// The earliest date the calendar offers a Stop: the latest earlier Stop date, and today without
// the past-dates permission.
const getMinDate = (drops: DuplicateCardValues['drops'], index: number, {
  canSetPastDates,
  today
}: DuplicateDateRules) => [canSetPastDates ? undefined : today, ...drops.slice(0, index).map(stop => stop.date)].reduce<Date | undefined>((latest, date) => date && (!latest || date > latest) ? date : latest, undefined);

/**
 * One Duplicate: each Stop's date, time and reference in Route order. The grid is a ledger, one row
 * per Stop, and leaves the rate to the card's title line (`DuplicateCardRate`); the column groups
 * the Stops under "Dates and references", with only the first Stop's reference, and the rate under
 * "Rate".
 */
export const DuplicateCard = withFieldGroup({
  defaultValues: DEFAULT_VALUES,
  props: DUPLICATE_CARD_PROPS,
  render: function Render({
    dateRules,
    datesOption,
    error,
    group,
    layout,
    pending,
    rateOption,
    templateStops
  }) {
    const drops = useSelector(group.store, state => state.values.drops);
    // Several cards render at once, so their element ids need their own prefix.
    const id = useId();
    const fieldGap = layout === 'grid' ? 'gap-3' : 'gap-1.5';
    const stopKind = (index: number) => drops[index]?.type === 'collection' ? 'Collection' : 'Delivery';
    const reference = (index: number) => <group.Field name={`drops[${index}].reference_no`} data-sentry-element="group.Field" data-sentry-component="reference" data-sentry-source-file="duplicate-card.tsx">
        {field => <Field className={fieldGap}>
            <FieldLabel htmlFor={`${id}-reference-${index}`}>Reference number</FieldLabel>
            <Input disabled={pending} id={`${id}-reference-${index}`} onBlur={field.handleBlur} onChange={event => field.handleChange(event.target.value || null)} placeholder="Enter a new reference number" value={field.state.value ?? ''} />
          </Field>}
      </group.Field>;
    const stopFields = (index: number, children: ReactNode) => <DuplicateStopFields allDay={templateStops[index]?.time?.type === '24'} fields={`drops[${index}]`} form={group} layout={layout} minDate={getMinDate(drops, index, dateRules)} pending={pending} stopNumber={index + 1} data-sentry-element="DuplicateStopFields" data-sentry-component="stopFields" data-sentry-source-file="duplicate-card.tsx">
        {children}
      </DuplicateStopFields>;
    if (layout === 'grid') {
      return <div className="@container/duplicate-card flex flex-col gap-4">
          {error}
          {/* The column names show once, 8px over the first row, so each field's own label is for
              screen readers only; the time column is as wide as its widest tabs. Narrower, the rows
              stack with their labels. */}
          <div className={cn(LEDGER_COLUMNS, 'gap-y-5 @min-[48rem]/duplicate-card:[&_[data-slot=field-label]]:sr-only')}>
            <div aria-hidden className="hidden text-sm font-medium text-muted-foreground @min-[48rem]/duplicate-card:contents [&>span]:-mb-3">
              <span />
              <span>Date</span>
              <span>Time</span>
              <span>Reference number</span>
            </div>
            {drops.map((stop, index) => {
            // Two Stops of one kind look alike, so each row starts with its number, filled for a
            // collection; its kind and place show beside it on a narrow card, and on hover.
            const name = [stopKind(index), templateStops[index]?.name].filter(Boolean).join(' · ');
            return <Fragment key={stop.id}>
                  <p className="flex items-center gap-2 self-start text-sm @min-[48rem]/duplicate-card:pt-1" title={name}>
                    <span aria-hidden className={cn('grid size-6 shrink-0 place-items-center rounded-full border text-xs font-semibold tabular-nums', stop.type === 'collection' ? 'border-primary bg-primary text-primary-foreground' : 'bg-background')}>
                      {index + 1}
                    </span>
                    <span className="@min-[48rem]/duplicate-card:sr-only">{name}</span>
                  </p>
                  {stopFields(index, reference(index))}
                </Fragment>;
          })}
          </div>
        </div>;
    }

    // A short stack of blocks to fill: each Stop, named as the dialog's rows name it, then the rate.
    return <div className="flex flex-col gap-4">
        {error}
        <section className="flex flex-col gap-2">
          <h3 className="text-base leading-none font-semibold">Dates and references</h3>
          {datesOption}
          {drops.map((stop, index) => <div className="flex flex-col gap-3 rounded-lg bg-background p-2.5 ring-1 ring-foreground/10" key={stop.id}>
              <p className="flex items-baseline gap-2 text-sm">
                <span className="font-semibold">
                  {index + 1}. {stopKind(index)}
                </span>
                <span className="truncate text-muted-foreground">{templateStops[index]?.name}</span>
              </p>
              {stopFields(index, index === 0 ? reference(0) : null)}
            </div>)}
        </section>
        <section className="flex flex-col gap-2">
          <h3 className="text-base leading-none font-semibold">Rate</h3>
          {rateOption}
          <div className="rounded-lg bg-background p-2.5 ring-1 ring-foreground/10">
            <DuplicateCardRate fields={{
            customer_rate_amount: 'customer_rate_amount',
            customer_rate_currency: 'customer_rate_currency'
          }} form={group} pending={pending} />
          </div>
        </section>
      </div>;
  }
});

/**
 * A folded card's line: the first Stop's date and window → the last Stop's | reference | rate.
 * It reads its own card, so a change to one card re-renders only that card's line.
 */
export const DuplicateCardSummary = withFieldGroup({
  defaultValues: DEFAULT_VALUES,
  render: function Render({
    group
  }) {
    const {
      date,
      details
    } = getDuplicateCardSummary(useSelector(group.store, state => state.values));
    return <span className="flex min-w-0 flex-1 items-center gap-2 text-sm font-medium text-muted-foreground">
        <CalendarIcon className="size-4 shrink-0" />
        <span className="truncate">
          {date ? <>
              <DateFormatter className="inline" date={date} kind="day-month" />,{' '}
            </> : null}
          {details}
        </span>
      </span>;
  }
});